// Deterministic fixtures for the offline mock API (plan §10).
//
// Vocabulary values are copied from the REAL platform vocabularies:
// - group ids ∈ GroupTypes (components/Risk/types.ts)
// - origin/consequence names ∈ the `/risk-groups` vocabulary (the same names
//   are enumerated under `form.risk.options.{origin,consequence}` in
//   public/locales/en/translation.json)
// - phase ids ∈ the 13 hardcoded phases (components/Risk/formdata.tsx)
// - consequence_id / phase_id use the pinned SEMICOLON-joined wire format
//   (api_contracts.md "Wire format").
//
// FIXTURE_DRAFT is unit-tested (fixtures.test.ts) to pass `validateDraft`
// with ZERO hard errors and EXACTLY ONE deliberate warning: the ESS iteration
// applies "EN ISO 13849-1", which is ⊄ the (empty) project standards →
// `standards_unknown`. Exactly one model warning and one skipped category are
// shipped alongside (idea.md: if a category has no measures, state why).

import { GroupId } from "components/Risk/types";
import { RiskGroup } from "pages/Risk/hooks/useRiskGroups";

import {
  AIDraftMitigation,
  AIDraftRisk,
  AIHazardSeverity,
  AIMethodologyWarning,
  AISkippedCategory,
} from "../types";

export interface FixtureShell {
  name: string;
  description: string;
  reasoning: string;
  severity: AIHazardSeverity;
  group_id_hint: GroupId;
}

export interface FixtureGroup {
  label: string;
  description: string;
  shells: FixtureShell[];
}

// 3 physical component/subsystem groups × 7 shells, covering all severities.
export const FIXTURE_GROUPS: FixtureGroup[] = [
  {
    label: "Protective enclosure",
    description: "Fixed guarding and access doors around the machine cell",
    shells: [
      {
        name: "Crushing at the access door",
        description:
          "The closing access door can crush fingers between the door edge and the frame.",
        reasoning:
          "The boundary data mentions a manually operated access door; doors of this type commonly create crushing points at the closing edge.",
        severity: "high",
        group_id_hint: "mechanical",
      },
      {
        name: "Reaching over the safety fence",
        description:
          "Persons can reach over the fence into the hazardous movement area if the fence height is insufficient.",
        reasoning:
          "Enclosures with operator interaction points are frequently undersized against the reach distances of ISO 13857.",
        severity: "medium",
        group_id_hint: "mechanical",
      },
    ],
  },
  {
    label: "Conveyor",
    description: "Belt conveyor feeding parts into the cell",
    shells: [
      {
        name: "Drawing-in at conveyor inlet nip",
        description:
          "The gap between the belt and the drive drum can draw in hands, hair or loose clothing.",
        reasoning:
          "Belt conveyors with accessible inlet nips are a classic drawing-in hazard per ISO 12100 Annex B.",
        severity: "high",
        group_id_hint: "mechanical",
      },
      {
        name: "Noise exposure at the conveyor drive",
        description:
          "The conveyor drive emits continuous noise at the adjacent workstation during operation.",
        reasoning:
          "Continuously running drives near manual workstations regularly exceed daily noise exposure action values.",
        severity: "low",
        group_id_hint: "noise",
      },
      {
        name: "Electric shock at the drive terminal box",
        description:
          "Live parts in the conveyor drive terminal box can be touched when the cover is removed.",
        reasoning:
          "Terminal boxes are opened during maintenance; missing or unsecured covers expose live parts.",
        severity: "medium",
        group_id_hint: "electrical",
      },
    ],
  },
  {
    label: "Robot arm",
    description: "Articulated robot handling parts inside the cell",
    shells: [
      {
        name: "Impact from unexpected robot movement",
        description:
          "The robot arm can strike a person inside the cell during unexpected start-up or program faults.",
        reasoning:
          "Whole-body access into robot cells combined with stored program logic makes unexpected movement a primary hazard.",
        severity: "high",
        group_id_hint: "mechanical",
      },
      {
        name: "Burn from hot gripper surfaces",
        description:
          "The gripper and handled parts remain hot after processing and can burn the skin on contact.",
        reasoning:
          "Parts leaving thermal processes keep contact temperatures above burn thresholds for several minutes.",
        severity: "low",
        group_id_hint: "thermal",
      },
    ],
  },
];

// Baseline assessment for the compliant draft:
// score = 3 × 2 × (3 + 4 + 2) = 54.
const FIXTURE_ASSESSMENT = {
  impact: 3,
  people_count: 2,
  duration: 3,
  detectability: 2,
  probability: 4,
};

const buildFixtureMitigations = (): AIDraftMitigation[] => [
  {
    id: "mock-mit-1",
    type: "INH",
    version: 1,
    description:
      "Reduce the nip gap to below 5 mm by fitting a filler bar at the drum inlet, eliminating the drawing-in point by design.",
    reasoning:
      "Category 1 (inherently safe design) must be exhausted first; closing the nip removes the hazard instead of guarding it.",
    left_risk:
      "Residual drawing-in risk only while the filler bar is removed during maintenance.",
    pictogram: "",
    applied_standards: [],
    impact: 3,
    people_count: 2,
    duration: 3,
    detectability: 2,
    probability: 3,
  },
  {
    id: "mock-mit-2",
    type: "EMS",
    version: 1,
    description:
      "Fixed mesh guard over the remaining drum area, removable only with tools.",
    reasoning:
      "Category 2 mechanical safeguarding for the area the filler bar cannot cover.",
    left_risk: "Access still possible with the guard dismounted.",
    pictogram: "",
    applied_standards: [],
    impact: 2,
    people_count: 2,
    duration: 3,
    detectability: 2,
    probability: 2,
  },
  {
    id: "mock-mit-3",
    type: "ESS",
    version: 1,
    description:
      "Interlocked inspection flap that stops the belt drive on opening; required performance level PLr = d per EN ISO 13849-1.",
    reasoning:
      "Category 2 control-technical measure, assessed in its own iteration as required (separate from the mechanical safeguard).",
    left_risk: "Failure of the interlock circuit.",
    pictogram: "",
    // Deliberate single validator warning: "EN ISO 13849-1" is not among the
    // project's selected standards → `standards_unknown` (acknowledgeable).
    applied_standards: ["EN ISO 13849-1"],
    impact: 2,
    people_count: 1,
    duration: 3,
    detectability: 2,
    probability: 2,
  },
  {
    id: "mock-mit-4",
    type: "PIK",
    version: 1,
    description:
      "ISO 7010 W024 'crushing of hands' warning sign at the conveyor inlet.",
    reasoning:
      "Category 3 information for use: the residual maintenance risk must be signposted.",
    left_risk: "Residual risk for personnel ignoring the warning.",
    pictogram: "W024",
    applied_standards: [],
    impact: 2,
    people_count: 1,
    duration: 2,
    detectability: 2,
    probability: 1,
  },
  {
    id: "mock-mit-5",
    type: "BDA",
    version: 1,
    description:
      "Operating manual chapter: lockout procedure before removing the filler bar or guard; training requirement for cleaning staff.",
    reasoning:
      "A PIK iteration must be followed by user-information measures (EN ISO 20607).",
    left_risk: "Low residual risk if the procedures are followed.",
    pictogram: "",
    applied_standards: [],
    impact: 1,
    people_count: 1,
    duration: 2,
    detectability: 2,
    probability: 1,
  },
];

// Full ISO-compliant draft: INH → EMS → ESS (own iteration, PLr note) →
// PIK → BDA, params monotonically non-increasing, reasoning + left_risk
// everywhere, fully_mitigated pinned false (user decides at the Accept Gate).
export const FIXTURE_DRAFT: AIDraftRisk = {
  readable_name: "Drawing-in at conveyor inlet nip",
  group_id: "mechanical",
  origin_id: "moving_component",
  consequence_id: "catch;squash",
  phase_id: "operations;cleaning;maintenance",
  description:
    "The inlet nip between the rotating drive drum and the belt is accessible from the side walkway. Hands, hair or loose clothing can be drawn in while the belt is moving.",
  protection_goal:
    "Prevent any body part or clothing from reaching the inlet nip while the belt is moving.",
  personal: "Operators, cleaning staff",
  ...FIXTURE_ASSESSMENT,
  assessment: { ...FIXTURE_ASSESSMENT },
  fully_mitigated: false,
  reasoning:
    "Generated by AI from the project description and boundary data; inlet nips on belt conveyors are a primary drawing-in hazard (ISO 12100, EN 620).",
  mitigations: buildFixtureMitigations(),
};

// Exactly ONE model-emitted methodology warning (latest generation, persisted).
export const FIXTURE_GENERATION_WARNINGS: AIMethodologyWarning[] = [
  {
    code: "new_hazard_introduced",
    message:
      "The fixed mesh guard adds a reach-over risk if mounted below 1400 mm — verify the safety distances per ISO 13857.",
    mitigation_index: 1,
  },
];

// Exactly ONE skipped category, with the reason stated (idea.md rule).
export const FIXTURE_SKIPPED_CATEGORIES: AISkippedCategory[] = [
  {
    category: "PSA",
    reason:
      "No residual risk remains that personal protective equipment would meaningfully reduce after the preceding measures.",
  },
];

// `/risk-groups` vocabulary subset matching FIXTURE_DRAFT's classification —
// used by the fixture unit tests; real names from the platform vocabulary.
export const FIXTURE_RISK_GROUPS: RiskGroup[] = [
  {
    riskGroup: { id: 1, name: "mechanical", order: 1 },
    origins: [
      { id: 1, name: "moving_component", order: 1 },
      { id: 2, name: "rotating_component", order: 2 },
      { id: 3, name: "sharp_end", order: 3 },
    ],
    consequences: [
      { id: 1, name: "catch", order: 1 },
      { id: 2, name: "squash", order: 2 },
      { id: 3, name: "cut", order: 3 },
      { id: 4, name: "shear", order: 4 },
    ],
  },
];

const cloneDraft = (draft: AIDraftRisk): AIDraftRisk => ({
  ...draft,
  assessment: { ...draft.assessment },
  mitigations: draft.mitigations.map((mitigation) => ({
    ...mitigation,
    applied_standards: [...(mitigation.applied_standards || [])],
  })),
});

// Tailors the fixture draft to the hazard being generated (mock parity with
// "the server persists the freshest shell values onto the shell"): free-text
// name/description follow the shell, everything validated stays fixture-exact
// so the compliant warning profile holds for every hazard.
export const buildDraftForShell = (
  name: string,
  description: string
): AIDraftRisk => {
  const draft = cloneDraft(FIXTURE_DRAFT);

  if (name.trim()) {
    draft.readable_name = name;
  }
  if (description.trim()) {
    draft.description = description;
  }

  return draft;
};

// Deterministic regeneration variant (plan §10): given the hazard's CURRENT
// draft it changes the description (basics), ONE assessment param
// (assessment), ONE existing mitigation (mitigation:1 changed) and ADDS one
// mitigation (mitigation_added) — classification, reasoning and the remaining
// mitigation pairs stay identical, so the diff view shows every section kind.
export const buildRegenerationProposal = (
  current: AIDraftRisk
): AIDraftRisk => {
  const proposal = cloneDraft(current);

  // basics → changed
  proposal.description = `${current.description} Regeneration note: access during cleaning shifts was re-assessed with the additional context.`;

  // assessment → changed (stays within range; never increases a param, so no
  // monotonicity warning is introduced against the first iteration).
  proposal.probability = Math.max(1, current.probability - 1);
  proposal.assessment = {
    impact: proposal.impact,
    people_count: proposal.people_count,
    duration: proposal.duration,
    detectability: proposal.detectability,
    probability: proposal.probability,
  };

  // mitigation:1 → changed pair (free text only; params untouched)
  if (proposal.mitigations[1]) {
    proposal.mitigations[1] = {
      ...proposal.mitigations[1],
      description: `${proposal.mitigations[1].description} Mesh size reduced to 20 mm per ISO 13857 reach distances.`,
    };
  }

  // appended mitigation → mitigation_added (params ≤ last iteration).
  proposal.mitigations.push({
    id: `mock-mit-${proposal.mitigations.length + 1}`,
    type: "PSA",
    version: 1,
    description:
      "Cut-resistant gloves (EN 388) mandated for cleaning tasks at the conveyor inlet.",
    reasoning:
      "PPE addresses the residual cleaning-phase risk that remains after all higher-priority measures.",
    left_risk: "Low residual risk while gloves are worn.",
    pictogram: "",
    applied_standards: [],
    impact: 1,
    people_count: 1,
    duration: 2,
    detectability: 2,
    probability: 1,
  });

  return proposal;
};
